import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import axios from '../../services/api-axios';

function FinishedGameDetailsModal({ id, onClose }) {
    const [loading, setLoading] = useState(true);
    const [gameData, setGameData] = useState(null);

    useEffect(() => {
        if (!id) return;

        setLoading(true);
        axios.get(`/rest/game/finishedGame/${id}`)
            .then((res) => {
                console.log("Game details fetched successfully:", res.data);
                setGameData(res.data);
            })
            .catch((err) => {
                console.error("Error fetching data:", err);
            })
            .finally(() => setLoading(false));
    }, [id]);

    const formatCards = (cardsString) => {
        if (!cardsString) return [];
        return cardsString.split(',').map(card => {
            const [suit, value] = card.trim().split(' ');
            const suitEmoji = {
                'heart': '♥️',
                'diamond': '♦️',
                'club': '♣️',
                'spade': '♠️'
            };
            return {
                suit: suitEmoji[suit] || suit,
                value: value,
                color: suit === 'heart' || suit === 'diamond' ? '#EF4444' : '#1F2937'
            };
        });
    };

    const CardComponent = ({ card }) => (
        <div style={{
            background: 'white',
            border: '2px solid #E5E7EB',
            borderRadius: '8px',
            padding: '4px 8px',
            margin: '2px',
            minWidth: '45px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600',
            color: card.color,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'inline-block'
        }}>
            <div>{card.suit}</div>
            <div>{card.value}</div>
        </div>
    );

    const PlayerSection = ({ playerName, round1Cards, round2Cards, round3Cards, isBot = false }) => {
        if (!playerName) return null;
        
        return (
            <div style={{
                background: 'rgba(75, 85, 99, 0.1)',
                border: '1px solid rgba(75, 85, 99, 0.2)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                }}>
                    <span style={{ fontSize: '16px' }}>
                        {isBot ? '🤖' : '👤'}
                    </span>
                    <h5 style={{
                        margin: 0,
                        color: '#E5E7EB',
                        fontSize: '16px',
                        fontWeight: '600'
                    }}>
                        {playerName}
                    </h5>
                </div>
                
                {[
                    { label: 'Round 1', cards: round1Cards },
                    { label: 'Round 2', cards: round2Cards },
                    { label: 'Round 3', cards: round3Cards }
                ].map(({ label, cards }) => (
                    <div key={label} style={{ marginBottom: '8px' }}>
                        <div style={{
                            color: '#9CA3AF',
                            fontSize: '12px',
                            fontWeight: '600',
                            marginBottom: '4px'
                        }}>
                            {label}:
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                            {formatCards(cards).map((card, idx) => (
                                <CardComponent key={idx} card={card} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const TeamSection = ({ teamName, teamScore, totalCards, znkCount, has2ofClub, has10ofDiamond, players, isWinner }) => (
        <div style={{
            background: isWinner 
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)'
                : 'rgba(17, 24, 39, 0.3)',
            border: isWinner 
                ? '2px solid rgba(16, 185, 129, 0.3)'
                : '1px solid rgba(75, 85, 99, 0.3)',
            borderRadius: '16px',
            padding: '20px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {isWinner && (
                <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    🏆 WINNER
                </div>
            )}
            
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
            }}>
                <div style={{
                    background: isWinner 
                        ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                    borderRadius: '12px',
                    padding: '12px',
                    fontSize: '20px'
                }}>
                    {isWinner ? '🥇' : '🎮'}
                </div>
                <div>
                    <h3 style={{
                        margin: '0 0 4px 0',
                        color: '#E5E7EB',
                        fontSize: '20px',
                        fontWeight: '700'
                    }}>
                        {teamName}
                    </h3>
                    <div style={{
                        display: 'flex',
                        gap: '16px',
                        alignItems: 'center',
                        fontSize: '14px',
                        color: '#9CA3AF'
                    }}>
                        <span style={{
                            background: isWinner ? 'rgba(16, 185, 129, 0.2)' : 'rgba(75, 85, 99, 0.3)',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontWeight: '600',
                            color: isWinner ? '#10B981' : '#E5E7EB'
                        }}>
                            Score: {teamScore}
                        </span>
                        <span>Cards: {totalCards}</span>
                        <span>Znk: {znkCount}</span>
                    </div>
                </div>
            </div>

            <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '16px',
                flexWrap: 'wrap'
            }}>
                <div style={{
                    background: has2ofClub ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    border: `1px solid ${has2ofClub ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                    borderRadius: '8px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: has2ofClub ? '#10B981' : '#EF4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}>
                    {has2ofClub ? '✅' : '❌'} 2 of Clubs
                </div>
                <div style={{
                    background: has10ofDiamond ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    border: `1px solid ${has10ofDiamond ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                    borderRadius: '8px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: has10ofDiamond ? '#10B981' : '#EF4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}>
                    {has10ofDiamond ? '✅' : '❌'} 10 of Diamonds
                </div>
            </div>

            <div>
                <h6 style={{
                    color: '#9CA3AF',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    Players
                </h6>
                {players.map((player, idx) => player)}
            </div>
        </div>
    );

    if (!id) return null;

    return (
        <Modal 
            show={!!id} 
            onHide={onClose}
            size="xl"
            centered
            style={{
                '--bs-modal-bg': 'rgba(0, 0, 0, 0.8)',
            }}
        >
            <div style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 35%, #0f3460 100%)',
                border: '1px solid rgba(75, 85, 99, 0.3)',
                borderRadius: '16px',
                color: 'white',
                fontFamily: '"Inter", system-ui, sans-serif',
                maxHeight: '90vh',
                overflow: 'hidden'
            }}>
                <Modal.Header 
                    closeButton 
                    style={{
                        background: 'rgba(17, 24, 39, 0.8)',
                        backdropFilter: 'blur(20px)',
                        border: 'none',
                        borderBottom: '1px solid rgba(75, 85, 99, 0.3)',
                        padding: '20px 24px'
                    }}
                >
                    <Modal.Title style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '24px',
                        fontWeight: '700'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                            borderRadius: '12px',
                            padding: '8px',
                            fontSize: '20px'
                        }}>
                            📊
                        </div>
                        Game Summary
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body style={{
                    padding: '24px',
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    background: 'transparent'
                }}>
                    {loading ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '60px',
                            color: '#9CA3AF'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '16px'
                            }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    border: '3px solid rgba(16, 185, 129, 0.3)',
                                    borderTop: '3px solid #10B981',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }} />
                                <span>Loading game details...</span>
                            </div>
                        </div>
                    ) : gameData ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            {/* Game Info Header */}
                            <div style={{
                                background: 'rgba(17, 24, 39, 0.5)',
                                border: '1px solid rgba(75, 85, 99, 0.3)',
                                borderRadius: '12px',
                                padding: '16px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '12px'
                            }}>
                                <div>
                                    <div style={{ color: '#9CA3AF', fontSize: '12px', marginBottom: '4px' }}>
                                        Game ID
                                    </div>
                                    <div style={{ 
                                        fontFamily: 'monospace', 
                                        fontSize: '14px', 
                                        color: '#E5E7EB',
                                        fontWeight: '600'
                                    }}>
                                        {gameData.id}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: '#9CA3AF', fontSize: '12px', marginBottom: '4px' }}>
                                        Lobby ID
                                    </div>
                                    <div style={{ 
                                        fontFamily: 'monospace', 
                                        fontSize: '14px', 
                                        color: '#E5E7EB',
                                        fontWeight: '600'
                                    }}>
                                        {gameData.lobbyId}
                                    </div>
                                </div>
                            </div>

                            {/* Teams */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
                                gap: '20px'
                            }}>
                                <TeamSection
                                    teamName={gameData.team1}
                                    teamScore={gameData.team1Score}
                                    totalCards={gameData.team1TotalCards}
                                    znkCount={gameData.team1ZnkCount}
                                    has2ofClub={gameData.team1Has2ofClub}
                                    has10ofDiamond={gameData.team1Has10ofDiamond}
                                    isWinner={gameData.team1Score > gameData.team2Score}
                                    players={[
                                        <PlayerSection
                                            key="team1p1"
                                            playerName={gameData.team1Player1}
                                            round1Cards={gameData.team1Player1Round1Cards}
                                            round2Cards={gameData.team1Player1Round2Cards}
                                            round3Cards={gameData.team1Player1Round3Cards}
                                            isBot={gameData.team1Player1?.includes('#BOT#')}
                                        />,
                                        <PlayerSection
                                            key="team1p2"
                                            playerName={gameData.team1Player2}
                                            round1Cards={gameData.team1Player2Round1Cards}
                                            round2Cards={gameData.team1Player2Round2Cards}
                                            round3Cards={gameData.team1Player2Round3Cards}
                                            isBot={gameData.team1Player2?.includes('#BOT#')}
                                        />
                                    ].filter(Boolean)}
                                />

                                <TeamSection
                                    teamName={gameData.team2}
                                    teamScore={gameData.team2Score}
                                    totalCards={gameData.team2TotalCards}
                                    znkCount={gameData.team2ZnkCount}
                                    has2ofClub={gameData.team2Has2ofClub}
                                    has10ofDiamond={gameData.team2Has10ofDiamond}
                                    isWinner={gameData.team2Score > gameData.team1Score}
                                    players={[
                                        <PlayerSection
                                            key="team2p1"
                                            playerName={gameData.team2Player1}
                                            round1Cards={gameData.team2Player1Round1Cards}
                                            round2Cards={gameData.team2Player1Round2Cards}
                                            round3Cards={gameData.team2Player1Round3Cards}
                                            isBot={gameData.team2Player1?.includes('#BOT#')}
                                        />,
                                        <PlayerSection
                                            key="team2p2"
                                            playerName={gameData.team2Player2}
                                            round1Cards={gameData.team2Player2Round1Cards}
                                            round2Cards={gameData.team2Player2Round2Cards}
                                            round3Cards={gameData.team2Player2Round3Cards}
                                            isBot={gameData.team2Player2?.includes('#BOT#')}
                                        />
                                    ].filter(Boolean)}
                                />
                            </div>

                            {/* Game Progression */}
                            {gameData.sliceList && gameData.sliceList.length > 0 && (
                                <div style={{
                                    background: 'rgba(17, 24, 39, 0.5)',
                                    border: '1px solid rgba(75, 85, 99, 0.3)',
                                    borderRadius: '12px',
                                    padding: '20px'
                                }}>
                                    <h6 style={{
                                        color: '#E5E7EB',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        marginBottom: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        🎯 Game Progression
                                    </h6>
                                    <div style={{ 
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                        gap: '12px'
                                    }}>
                                        {gameData.sliceList.map((slice, idx) => {
                                            // Parse the slice format: "TEAM: team2R: 3 , T: 14 CARDS: diamond A,heart 10,spade 10"
                                            const match = slice.match(/TEAM:\s*(\w+)\s*,\s*R:\s*(\d+)\s*,\s*T:\s*(\d+)\s*,\s*CARDS:\s*(.+)/);
                                            if (!match) {
                                                return (
                                                    <div key={idx} style={{
                                                        background: 'rgba(75, 85, 99, 0.2)',
                                                        border: '1px solid rgba(75, 85, 99, 0.3)',
                                                        borderRadius: '8px',
                                                        padding: '12px',
                                                        fontSize: '14px',
                                                        color: '#E5E7EB',
                                                        fontFamily: 'monospace'
                                                    }}>
                                                        {slice}
                                                    </div>
                                                );
                                            }

                                            const [, team, round, turn, cardsString] = match;
                                            const cards = formatCards(cardsString);
                                            const isTeam1 = team.toLowerCase().includes('team1') || team.toLowerCase().includes('1');
                                            const teamColor = isTeam1 
                                                ? 'rgba(16, 185, 129, 0.2)' 
                                                : 'rgba(99, 102, 241, 0.2)';
                                            const teamBorderColor = isTeam1 
                                                ? 'rgba(16, 185, 129, 0.4)' 
                                                : 'rgba(99, 102, 241, 0.4)';
                                            const teamTextColor = isTeam1 
                                                ? '#10B981' 
                                                : '#6366F1';

                                            return (
                                                <div key={idx} style={{
                                                    background: teamColor,
                                                    border: `1px solid ${teamBorderColor}`,
                                                    borderRadius: '12px',
                                                    padding: '16px',
                                                    position: 'relative',
                                                    overflow: 'hidden'
                                                }}>
                                                    {/* Header */}
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        marginBottom: '12px'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px'
                                                        }}>
                                                            <div style={{
                                                                background: teamTextColor,
                                                                color: 'white',
                                                                borderRadius: '8px',
                                                                padding: '4px 8px',
                                                                fontSize: '12px',
                                                                fontWeight: '700',
                                                                textTransform: 'uppercase'
                                                            }}>
                                                                {team}
                                                            </div>
                                                            <div style={{
                                                                color: '#E5E7EB',
                                                                fontSize: '12px',
                                                                opacity: 0.8
                                                            }}>
                                                                Turn #{idx + 1}
                                                            </div>
                                                        </div>
                                                        
                                                        <div style={{
                                                            display: 'flex',
                                                            gap: '8px',
                                                            alignItems: 'center'
                                                        }}>
                                                            <div style={{
                                                                background: 'rgba(17, 24, 39, 0.4)',
                                                                borderRadius: '6px',
                                                                padding: '4px 8px',
                                                                fontSize: '11px',
                                                                color: '#9CA3AF',
                                                                fontWeight: '600'
                                                            }}>
                                                                Round {round}
                                                            </div>
                                                            <div style={{
                                                                background: 'rgba(17, 24, 39, 0.4)',
                                                                borderRadius: '6px',
                                                                padding: '4px 8px',
                                                                fontSize: '11px',
                                                                color: '#9CA3AF',
                                                                fontWeight: '600'
                                                            }}>
                                                                Turn {turn}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Cards */}
                                                    <div>
                                                        <div style={{
                                                            color: '#9CA3AF',
                                                            fontSize: '11px',
                                                            fontWeight: '600',
                                                            marginBottom: '6px',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            Cards Played ({cards.length})
                                                        </div>
                                                        <div style={{ 
                                                            display: 'flex', 
                                                            flexWrap: 'wrap', 
                                                            gap: '4px' 
                                                        }}>
                                                            {cards.map((card, cardIdx) => (
                                                                <div key={cardIdx} style={{
                                                                    background: 'white',
                                                                    border: '2px solid #E5E7EB',
                                                                    borderRadius: '6px',
                                                                    padding: '3px 6px',
                                                                    minWidth: '35px',
                                                                    textAlign: 'center',
                                                                    fontSize: '10px',
                                                                    fontWeight: '700',
                                                                    color: card.color,
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                    display: 'inline-block'
                                                                }}>
                                                                    <div style={{ lineHeight: '1' }}>{card.suit}</div>
                                                                    <div style={{ lineHeight: '1' }}>{card.value}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Decorative element */}
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '-2px',
                                                        right: '-2px',
                                                        width: '20px',
                                                        height: '20px',
                                                        background: `linear-gradient(135deg, ${teamTextColor}20, transparent)`,
                                                        borderRadius: '0 12px 0 12px'
                                                    }} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '60px',
                            color: '#9CA3AF'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '16px'
                            }}>
                                <div style={{ fontSize: '48px' }}>⚠️</div>
                                <span>Failed to load game details</span>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                
                <Modal.Footer style={{
                    background: 'rgba(17, 24, 39, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: 'none',
                    borderTop: '1px solid rgba(75, 85, 99, 0.3)',
                    padding: '16px 24px'
                }}>
                    <Button 
                        variant="secondary" 
                        onClick={onClose}
                        style={{
                            background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '10px 20px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 25px rgba(107, 114, 128, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        🏠 Return to Lobby
                    </Button>
                </Modal.Footer>
            </div>
            
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .modal-content {
                    background: transparent !important;
                    border: none !important;
                }
                
                .btn-close {
                    filter: invert(1) !important;
                    opacity: 0.8 !important;
                }
                
                .btn-close:hover {
                    opacity: 1 !important;
                }
            `}</style>
        </Modal>
    );
}

export default FinishedGameDetailsModal;